import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import type { ClientCreditItem, DisputeRound } from './disputes.types';

/**
 * Returns the API URL for downloading a dispute letter PDF.
 */
export function getLetterUrl(roundId: string): string {
  const base = import.meta.env.VITE_API_BASE_URL || 'http://api.localhost/api/v1';
  return `${base}/disputes/${roundId}/letter`;
}

export function useCreditItems(clientId: string | null | undefined) {
  return useQuery({
    enabled: Boolean(clientId),
    queryKey: ['credit-items', clientId],
    queryFn: async () => {
      const r = await api.get<ClientCreditItem[]>(
        `/clients/${clientId}/credit-items`,
      );
      return r.data;
    },
  });
}

export function useDisputeRounds(clientId: string | null | undefined) {
  return useQuery({
    enabled: Boolean(clientId),
    queryKey: ['disputes', 'list', clientId],
    queryFn: async () => {
      const r = await api.get<DisputeRound[]>(
        `/clients/${clientId}/disputes`,
      );
      return r.data;
    },
  });
}

export function useDisputeRound(roundId: string | null | undefined) {
  return useQuery({
    enabled: Boolean(roundId),
    queryKey: ['disputes', 'detail', roundId],
    queryFn: async () => {
      const r = await api.get<DisputeRound>(`/disputes/${roundId}`);
      return r.data;
    },
  });
}

export function useCreateDisputeRound(clientId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      clientCreditItemIds: string[];
      disputeReason: string;
      notes?: string;
    }) => {
      const r = await api.post(`/clients/${clientId}/disputes`, payload);
      return r.data as DisputeRound;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['disputes', 'list', clientId] });
      void qc.invalidateQueries({ queryKey: ['credit-items', clientId] });
    },
  });
}
